import { currentUser, batchDataChanges } from './database';
import { services, saveOnboarding, ensureWorkspace, loadBrain } from './productBrain';
import { analyzeOnboardingBatch } from './ollama';
import { jiraDestination, sourceId } from '../lib/sourceMapping';
import { assertImportOwner, routeSource } from './workspaceImport';

const clip = (value, length = 1400) => String(value || '').slice(0, length);
const sourceContent = source => [source.title, source.content, source.metadata];
// Firestore maps have no field order. Hash their content consistently across
// collection queries, individual reads, and cached snapshots.
const canonicalJSON = value => JSON.stringify(value, (_key, item) => item && typeof item === 'object' && !Array.isArray(item)
  ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item);
const sourceVersion = async (ownerId, source) => `v2:${await sourceId(ownerId, 'source-version', canonicalJSON(sourceContent(source)))}`;

async function verifySourceVersion(ownerId, source, insight) {
  const version = await sourceVersion(ownerId, source);
  const previous = insight.proposal.source_version;
  if (previous === version) return version;
  // Existing suggestions used an order-sensitive UUID hash. Keep them usable
  // when the recorded source write predates this analysis, and upgrade their
  // hash before applying so a partially completed write can also be retried.
  if (/^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(previous || '')) {
    const legacyVersion = await sourceId(ownerId, 'source-version', JSON.stringify(sourceContent(source)));
    if (previous === legacyVersion) return version;
    const run = (await ensureWorkspace()).onboarding?.analysis;
    const sourceUpdated = Date.parse(source.updated_at);
    const analysisStarted = Date.parse(run?.started_at);
    if (run?.id === insight.analysis_id && Number.isFinite(sourceUpdated) && Number.isFinite(analysisStarted) && sourceUpdated <= analysisStarted) return version;
  }
  throw new Error('This source changed after the analysis. Analyze again before applying the suggestion.');
}

export function analysisRecords(data) {
  const sources = data.imports || [];
  const mapped = new Set(sources.filter(s => s.mapped_id).map(s => `${s.mapped_type}:${s.mapped_id}`));
  const sourcesByParent = new Map();
  for (const source of sources) {
    const parent = source.metadata?.parent_key;
    if (!parent) continue;
    const key = `${source.metadata.site_url}:${parent}`;
    const children = sourcesByParent.get(key) || [];
    children.push(source); sourcesByParent.set(key, children);
  }
  const records = sources.map(source => {
    const children = sourcesByParent.get(`${source.metadata?.site_url}:${source.metadata?.key}`) || [];
    return { type: 'imports', id: source.id, title: source.title, description: clip(source.content),
      metadata: source.metadata, default_destination: source.source === 'jira' ? jiraDestination(source.metadata) : 'inbox',
      mapped_type: source.mapped_type || '', mapped_id: source.mapped_id || '',
      child_summary: { total: children.length, bugs: children.filter(child => jiraDestination(child.metadata) === 'issues').length,
        completed: children.filter(child => child.metadata?.status_category === 'done').length,
        examples: children.slice(0, 4).map(child => ({ key: child.metadata.key, title: clip(child.title, 160), status: child.metadata.status })) },
    };
  });
  for (const type of ['initiatives','issues','problems','evidence','objectives','inbox','time']) {
    for (const record of data[type] || []) {
      if (mapped.has(`${type}:${record.id}`)) continue;
      records.push({ type, id: record.id, title: clip(record.title || record.activity_title, 300),
        description: clip(record.description || record.content || record.expected_impact), status: record.status || '',
        time_horizon: record.time_horizon || '', priority: record.priority || '',
        ...(type === 'time' ? { date: record.date, duration_minutes: record.duration_minutes, category: record.category } : {}),
        ...(type === 'evidence' ? { strength: record.strength, problem_card_id: record.problem_card_id } : {}),
      });
    }
  }
  return records;
}

export async function analyzeWorkspace({ model, signal, onProgress = () => {} }) {
  if (!model) throw new Error('Choose an AI provider and model.');
  const ownerId = (await currentUser()).id;
  const { data, errors } = await loadBrain();
  if (Object.keys(errors).length) throw new Error('Some workspace data could not be loaded. Resolve the data errors before analyzing.');
  const records = analysisRecords(data);
  if (!records.length) throw new Error('Import a source or add workspace records before running the analysis.');
  const fingerprint = await sourceId(ownerId, 'analysis', JSON.stringify(records));
  const workspace = await ensureWorkspace();
  const previous = workspace.onboarding?.analysis;
  const resumable = previous && previous.model === model && previous.fingerprint === fingerprint && previous.status !== 'complete';
  const run = resumable ? { ...previous } : { id: crypto.randomUUID(), model, fingerprint, completed: 0, total: records.length, batches: Math.ceil(records.length / 8), started_at: new Date().toISOString() };
  run.status = 'running';
  const objectives = records.filter(record => record.type === 'objectives').slice(0, 3);
  const summary = { type: 'workspace', id: ownerId, title: 'Workspace totals',
    imported_sources: (data.imports || []).length, bugs: (data.imports || []).filter(s => jiraDestination(s.metadata) === 'issues').length,
    initiatives: data.initiatives.length, issues: data.issues.length, evidence: data.evidence.length,
    time_minutes: (data.time || []).reduce((total, entry) => total + (Number(entry.duration_minutes) || 0), 0),
    scope: 'Totals describe saved records. Detailed analysis uses batches; absence in a batch is not proof of absence in the workspace.' };
  await saveOnboarding({ step: 2, analysis: run });
  try {
    for (let offset = run.completed; offset < records.length; offset += 8) {
      await assertImportOwner(ownerId, signal);
      const batch = records.slice(offset, offset + 8);
      const context = [summary, ...batch, ...objectives.filter(o => !batch.some(r => r.type === o.type && r.id === o.id))];
      onProgress({ ...run, status: `Analyzing records ${offset + 1}–${offset + batch.length} of ${records.length}` });
      const { answer } = await analyzeOnboardingBatch({ model, context, signal });
      await assertImportOwner(ownerId, signal);
      await batchDataChanges(async () => {
        const save = async (key, record) => {
          const id = await sourceId(ownerId, 'analysis-result', `${run.id}:${key}`);
          if (!await services.insights.getById(id)) await services.insights.create({ ...record, id, model, analysis_id: run.id, status: 'suggested' });
        };
        for (const mapping of answer.mappings) {
          const source = batch.find(r => r.type === 'imports' && r.id === mapping.source_id);
          if (!source) throw new Error('The model returned an unknown source. Retry the analysis.');
          await save(`mapping:${source.id}`, { kind: 'mapping', area: mapping.destination === 'initiatives' ? 'roadmap' : mapping.destination === 'issues' ? 'firefighter' : 'discovery',
            title: source.title, detail: mapping.reason, references: [{ type: 'imports', id: source.id }],
            proposal: { ...mapping, source_version: await sourceVersion(ownerId, data.imports.find(row => row.id === source.id)) } });
        }
        for (const [index, insight] of answer.insights.entries()) await save(`insight:${offset}:${index}`, { ...insight, kind: 'insight' });
      });
      run.completed = offset + batch.length;
      await saveOnboarding({ analysis: { ...run } });
    }
    run.status = 'complete'; run.finished_at = new Date().toISOString();
    await saveOnboarding({ analysis: run }); onProgress(run);
    return run;
  } catch (error) {
    run.status = signal?.aborted ? 'cancelled' : 'failed';
    if ((await currentUser()).id === ownerId) await saveOnboarding({ analysis: run });
    throw error;
  }
}

export async function applyMappings(insightIds, { signal, onProgress = () => {} } = {}) {
  const ownerId = (await currentUser()).id;
  const ids = [...new Set(insightIds)];
  let result = { total: ids.length, completed: 0, applied: [], failed: [] };
  onProgress(result);
  // Refresh the workspace once after the batch, instead of reloading every
  // collection between suggestions while remote writes are still in flight.
  await batchDataChanges(async () => {
    for (const id of ids) {
      await assertImportOwner(ownerId, signal);
      try {
        await applyMapping(id, { ownerId, signal });
        result = { ...result, applied: [...result.applied, id] };
      } catch (error) {
        // Cancellation and account changes stop the batch; an individual stale
        // or conflicting suggestion must not block the other selected records.
        await assertImportOwner(ownerId, signal);
        result = { ...result, failed: [...result.failed, { id, message: error.message || 'Could not save this suggestion. Try again.' }] };
      }
      result = { ...result, completed: result.completed + 1 };
      onProgress(result);
    }
  });
  return result;
}

export async function applyMapping(insightId, { ownerId, signal } = {}) {
  ownerId = ownerId || (await currentUser()).id;
  await assertImportOwner(ownerId, signal);
  const insight = await services.insights.getById(insightId);
  if (!insight || insight.kind !== 'mapping') throw new Error('This mapping is no longer available.');
  if (insight.status === 'applied') return;
  if (insight.status !== 'suggested') throw new Error('This suggestion was dismissed. Analyze again for new suggestions.');
  const proposal = insight.proposal;
  let source = await services.imports.getById(proposal?.source_id);
  if (!source) throw new Error('The source record no longer exists. Import it again.');
  const version = await verifySourceVersion(ownerId, source, insight);
  const fixed = source.source === 'jira' ? jiraDestination(source.metadata) : 'inbox';
  if (fixed !== 'inbox' && fixed !== proposal.destination) throw new Error('The source issue type has changed. Analyze it again.');
  if (source.mapped_type === 'inbox' && source.mapped_id) {
    const capture = await services.inbox.getById(source.mapped_id);
    if (capture?.status === 'processed') {
      const destination = { issue: 'issues', initiative: 'initiatives' }[capture.destination];
      if (destination !== proposal.destination || !capture.destination_id) throw new Error('This source was already organized in Inbox. Review its existing destination before applying another mapping.');
      source = { ...source, mapped_type: destination, mapped_id: capture.destination_id };
    }
  }
  await batchDataChanges(async () => {
    await assertImportOwner(ownerId, signal);
    if (proposal.source_version !== version) await services.insights.update(insight.id, { proposal: { ...proposal, source_version: version } });
    await routeSource(source, proposal.destination, ownerId, { signal, priority: proposal, model: insight.model });
    await assertImportOwner(ownerId, signal);
    await services.insights.update(insight.id, { status: 'applied' });
  });
}
